// src/components/document/NumberingPreview.tsx
// Live preview of document number based on numbering scheme.

import { useState, useEffect } from 'react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';

interface NumberingPreviewProps {
  schemeId: string;
  prefix?: string;
}

export function NumberingPreview({ schemeId, prefix }: NumberingPreviewProps) {
  const { t } = useI18n();
  const store = useDocument();
  const scope = useScope();
  const [preview, setPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!schemeId) {
      setPreview(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const result = await store.PreviewDocumentNumber(schemeId, {
          scheme: { id: schemeId } as any,
        } as any);
        if (!cancelled) {
          setPreview(result?.document_number || null);
        }
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    const timer = setTimeout(load, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [schemeId, prefix, store, scope.productId]);

  return (
    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-4 h-4 text-muted-foreground flex-shrink-0">
        <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="text-sm">
        <span className="text-muted-foreground">{t('documentPolicy.preview')}:</span>{' '}
        {isLoading ? (
          <span className="text-muted-foreground animate-pulse">...</span>
        ) : preview ? (
          <span className="font-mono text-foreground">{preview}</span>
        ) : (
          <span className="text-muted-foreground italic">{t('documentPolicy.noPreview')}</span>
        )}
      </div>
    </div>
  );
}
